import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { useTranslation } from "react-i18next";

/*
 * The primitives, kept here because that is where both other dashboards keep
 * theirs and there is no package between the repositories to share them
 * through — see CONVENTIONS.md. This is a *subset*: the vocabulary is identical
 * so a component moves between the three without translation, and the pieces
 * this dashboard has no use for are simply absent rather than copied and left
 * unreferenced.
 *
 * The rule that governs everything below: reach for a semantic token, never a
 * numbered scale. A class naming a token the theme does not define renders as
 * nothing at all, which is the failure mode this workspace chose on purpose —
 * a bug you see the first time you look at the screen beats one that degrades
 * politely and survives review.
 */

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// ----------------------------------------------------------------- buttons

/*
 * One ladder, and nothing between the rungs. Copied from the customer
 * dashboard because the vocabulary must not drift: `primary` is the one action
 * you opened the screen to take, `outline` is promoted but sharing the screen
 * with a primary, `secondary` has a peer beside it, `ghost` is row-level, and
 * `danger` is destructive and only ever destructive.
 *
 * `primary` is the inverse block rather than the accent, which is what frees
 * blue for links, active tabs and focus. So a blue thing on any Maxlayer screen
 * is something you can navigate to — and this dashboard runs the customer blue,
 * for the reason `index.css` sets out.
 */
type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  /*
   * Disabled drops the fill rather than fading it, which is the one button
   * state a light theme cannot inherit.
   *
   * The dark repositories use `disabled:opacity-40`, and on near-black a white
   * block at 40% is plainly a faded thing. On white, ink at 40% is a solid mid
   * grey — a button that looks entirely enabled and simply refuses to respond.
   * A recessed fill with muted text says "not now" the way the rest of this
   * theme says it.
   */
  primary:
    "bg-inverse text-on-inverse hover:opacity-90 disabled:bg-raised disabled:text-fg-subtle disabled:hover:opacity-100",
  secondary:
    "border border-line bg-transparent text-fg hover:border-line-strong hover:bg-raised disabled:text-fg-subtle disabled:hover:bg-transparent",
  /* The border alphas are heavier than the dark repositories' /40 and /30. A
     partially transparent colour over near-black darkens toward the ground and
     stays visible; over white it washes out, so the same /40 that reads as a
     border there reads as a smudge here. */
  outline:
    "border border-accent/55 bg-transparent text-accent hover:border-accent hover:bg-accent-soft disabled:opacity-40",
  ghost:
    "text-fg-muted hover:bg-raised hover:text-fg disabled:text-fg-subtle disabled:hover:bg-transparent",
  danger:
    "border border-danger/55 bg-transparent text-danger hover:bg-danger/8 hover:border-danger disabled:opacity-40",
};

/*
 * The second height in each pair is the thumb's. 28 and 32px are comfortable
 * under a cursor that lands where it is pointed and miss under a finger.
 * `min-h` rather than `h`, so a button whose label wraps on a narrow screen
 * grows instead of clipping it.
 */
const BUTTON_SIZES = {
  sm: "h-7 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-1.5 px-2.5 text-[13px]",
  md: "h-8 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-3 text-[13px]",
  lg: "h-10 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-4 text-sm",
  xl: "h-11 pointer-coarse:h-auto pointer-coarse:min-h-11 gap-2 px-5 text-sm",
};

/** Shared so an anchor or a `<Link>` can look like a button without nesting one. */
export function buttonStyles(
  variant: ButtonVariant = "primary",
  size: keyof typeof BUTTON_SIZES = "md",
  className?: string,
): string {
  return cx(
    "inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors duration-200 ease-smooth disabled:cursor-not-allowed",
    BUTTON_SIZES[size],
    BUTTON_VARIANTS[variant],
    className,
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: keyof typeof BUTTON_SIZES;
  loading?: boolean;
}

/*
 * Prefer adding a variant over passing colours through `className`: Tailwind
 * emits utilities grouped by property rather than in attribute order, so a
 * `bg-*` passed as a prop is not guaranteed to beat the variant's own
 * background. Two colour utilities for one property in a single class list is
 * always a bug, never a documented override.
 */
/**
 * `type="button"` unless a caller asks for otherwise.
 *
 * HTML defaults a button inside a form to `submit`, so every ordinary control
 * that happens to sit in one is a submit control. It surfaced in the customer
 * dashboard, where a copy button inside a wizard submitted the form with every
 * field still empty; the same default was here, waiting for the first button
 * that landed inside a form without asking to be one.
 *
 * Safe because every form in this app already marks its own submit control
 * explicitly. Pressing Enter in a text field still submits either way — that
 * path does not go through a button's type.
 */
export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={buttonStyles(variant, size, className)}
    >
      {loading && <Spinner className="size-3.5" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx("animate-spin", className ?? "size-5")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v3a5 5 0 0 0-5 5H4z"
      />
    </svg>
  );
}

// ------------------------------------------------------------------ panels

/**
 * A panel, and the one place this theme spends what the dark ones save.
 *
 * `maxlayer-clients` builds the same component flat — "no shadow, no gradient,
 * no inner highlight; depth comes from the hairline" — because on a near-black
 * canvas a shadow is mud. On white that argument reverses: a hairline alone
 * reads as a wireframe, and the softest possible shadow is what says this sits
 * *on* the page rather than being cut into it.
 *
 * `shadow-xs` and not a step more. The whole scale is tinted with the canvas's
 * neutral rather than black, so it reads as light landing on a surface.
 */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cx("rounded-lg border border-line bg-surface shadow-xs", className)}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
      <div className="min-w-0 space-y-1">
        <h2 className="text-sm font-medium text-fg">{title}</h2>
        {description && <p className="text-[13px] text-fg-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <h1 className="text-xl font-light tracking-[-0.02em] text-fg">{title}</h1>
        {description && <p className="max-w-2xl text-[13px] text-fg-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * One figure and its label.
 *
 * The unit is part of the value rather than the label — "$120.00" and not "120"
 * under a heading that says dollars — because these tiles sit in a row where a
 * money figure and a count are side by side, and a reader scanning them should
 * not have to check which is which.
 */
export function Stat({
  label,
  value,
  hint,
  tone = "default",
  size = "md",
  boxed = true,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** `muted` for a figure that is zero-by-design rather than zero-by-accident. */
  tone?: "default" | "muted";
  /**
   * Which row this belongs to, and the only hierarchy on the overview.
   *
   * Two panels of identically-sized figures read as one long undifferentiated
   * block — the money and the funnel look equally important because nothing
   * says otherwise. `lg` is the money, which is what somebody opened the page
   * for; `md` is everything measured alongside it.
   */
  size?: "md" | "lg";
  /** False inside a `StatGroup`, which supplies the box for all of them. */
  boxed?: boolean;
}) {
  return (
    <div
      className={cx(
        "space-y-1.5",
        size === "lg" ? "px-4 py-4 sm:px-5" : "px-4 py-3.5",
        boxed && "rounded-lg border border-line bg-surface shadow-xs",
      )}
    >
      <p className="micro">{label}</p>
      <p
        className={cx(
          "font-light tracking-[-0.025em] tabular-nums",
          size === "lg" ? "text-[1.75rem] leading-9" : "text-xl leading-7",
          tone === "muted" ? "text-fg-subtle" : "text-fg",
        )}
      >
        {value}
      </p>
      {/* Reserved rather than conditional: a row where one tile has a hint and
          its neighbour does not otherwise sits its figures at different
          heights, which reads as a rendering fault. */}
      <p className="min-h-[1.25rem] text-[12px] leading-5 text-fg-subtle">{hint}</p>
    </div>
  );
}

/**
 * Several figures read as one thing.
 *
 * The four money states are a *life cycle* — held becomes cleared becomes being
 * paid becomes paid — and four separately bordered boxes say the opposite: four
 * unrelated numbers, each important enough to have its own container. One panel
 * with hairlines between the columns is the ledger reading, and it is the house
 * one.
 *
 * The dividers are `divide-x` on the horizontal axis and `divide-y` when the row
 * has wrapped, which a single `divide` cannot express — hence the explicit pair
 * plus the border resets on the grid. Without them a two-column wrap on a phone
 * draws a rule down the outside edge.
 */
export function StatGroup({
  children,
  columns = 4,
}: {
  children: ReactNode;
  /** Four for the money life cycle, two for a pair. Nothing else is used. */
  columns?: 2 | 4;
}) {
  if (columns === 2) {
    return <Card className="grid grid-cols-2 divide-x divide-line">{children}</Card>;
  }
  return (
    <Card className="grid grid-cols-2 divide-line lg:grid-cols-4 lg:divide-x [&>*:nth-child(-n+2)]:border-b [&>*:nth-child(2n)]:border-l [&>*]:border-line lg:[&>*]:border-b-0 lg:[&>*]:border-l-0">
      {children}
    </Card>
  );
}

// ------------------------------------------------------------------ tables

/**
 * A wide table drops columns; it never becomes cards.
 *
 * `priority` names the narrowest viewport a column survives on, and the `Th`
 * and `Td` must agree or the row shears away from its header. Keep what
 * identifies the row and what you came to check; push what you would only read
 * *after* finding the row to `md` or `lg` — and never hide the only copy of a
 * fact on the screen.
 */
export type ColumnPriority = "always" | "sm" | "md" | "lg";

/* Written out rather than interpolated: Tailwind scans source text, so a
   template literal would emit nothing. */
const COLUMN_PRIORITY: Record<ColumnPriority, string> = {
  always: "",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
};

export function Table({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    // The region scrolls, never the page — and `scroll-shadow-x` says so only
    // when there is content past the edge. It needs its container on `surface`
    // with transparent rows, which is what `Card` provides.
    <div className="scroll-shadow-x overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead className="border-b border-line text-[11px] tracking-[0.08em] text-fg-subtle uppercase">
          {head}
        </thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  className,
  priority = "always",
}: {
  children?: ReactNode;
  className?: string;
  /** Must match the `Td` beneath it. */
  priority?: ColumnPriority;
}) {
  return (
    <th className={cx("px-4 py-2.5 font-medium", COLUMN_PRIORITY[priority], className)}>
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  title,
  colSpan,
  priority = "always",
}: {
  children?: ReactNode;
  className?: string;
  /** Native tooltip — used to hang an exact date off a relative one. */
  title?: string;
  colSpan?: number;
  /** Must match the `Th` above it. */
  priority?: ColumnPriority;
}) {
  return (
    <td className={cx("cell", COLUMN_PRIORITY[priority], className)} title={title} colSpan={colSpan}>
      {children}
    </td>
  );
}

/**
 * The foot of a keyset-paginated table.
 *
 * Not the `Pagination` component the other two dashboards share: that one
 * renders "1–25 of 340" and is told the total up front, and these lists have no
 * total — counting would be a second query over the same predicate for a number
 * no screen shows.
 *
 * Nothing renders on the last page, which is what the null cursor is for. A
 * disabled "Load more" under a complete list is furniture standing in for a
 * control, the same argument that component makes about a single page.
 *
 * `aria-live` because pressing it changes the table above without moving focus.
 * A screen reader is otherwise told nothing happened — and on an appending list
 * that is exactly the case where somebody presses it again.
 */
export function LoadMore({
  hasMore,
  loading,
  onMore,
  loaded,
  noun,
}: {
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
  /** How many rows are on screen, so the count means something before it is total. */
  loaded: number;
  /** A key under `common.nouns`, so the sentence reads in the page's language. */
  noun: "commissions" | "payouts" | "signups";
}) {
  const { t } = useTranslation("common");
  if (!hasMore) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
      <p className="text-[12px] text-fg-subtle" aria-live="polite">
        {t("showing", { count: loaded, noun: t(`nouns.${noun}`) })}
      </p>
      <Button variant="secondary" size="sm" loading={loading} onClick={onMore}>
        {t("loadMore")}
      </Button>
    </div>
  );
}

// ------------------------------------------------------------------ status

/**
 * State is a colour **and** a dot, never colour alone.
 *
 * The eye finds a red dot in a column of fifty rows far faster than it reads
 * the word "reversed", and the pairing survives being printed or read by
 * somebody who cannot separate the hues.
 *
 * The four state colours come off the semantic tokens and nowhere else, and
 * nothing decorative may borrow one: `mint` is done and good, `warn` is on its
 * way to trouble, `danger` has failed, and neutral is everything deliberately
 * not moving. A reserved colour only works if it is scarce.
 */
export type StatusTone = "live" | "warn" | "danger" | "neutral";

const STATUS_TONES: Record<StatusTone, { dot: string; text: string }> = {
  live: { dot: "bg-mint", text: "text-mint" },
  warn: { dot: "bg-warn", text: "text-warn" },
  danger: { dot: "bg-danger", text: "text-danger" },
  neutral: { dot: "bg-fg-subtle", text: "text-fg-muted" },
};

export function StatusBadge({ tone, label }: { tone: StatusTone; label: string }) {
  const styles = STATUS_TONES[tone];
  return (
    <span className={cx("inline-flex items-center gap-1.5 text-[13px]", styles.text)}>
      <span className={cx("size-1.5 shrink-0 rounded-full", styles.dot)} aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * A category rather than a state, and the one badge without a dot.
 *
 * The dot promises a reader this is live state worth scanning a column for.
 * Spending it on "which link" is how it stops working where it matters.
 */
export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-sm border border-line bg-raised px-1.5 py-0.5 font-mono text-[11px] text-fg-muted">
      {children}
    </span>
  );
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("font-mono text-[13px]", className)}>{children}</span>;
}

// ------------------------------------------------------------------- forms

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-fg">
        {label}
      </label>
      {children}
      {/* The error replaces the hint rather than stacking under it: two lines of
          small text under one field is a paragraph nobody reads, and the one
          that matters is the one that just appeared. */}
      {error ? (
        <p className="text-[12px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      /*
       * `bg-canvas` on a `surface` card, so the field is a step *down* from the
       * panel it sits on. That is the light-mode reading of `raised` described
       * in `index.css`: white is the ceiling, so an affordance says "type here"
       * by receding rather than by lifting. A white field on a white card needs
       * its border to do all the work, and a border heavy enough for that makes
       * a form of them look like a spreadsheet.
       */
      className={cx(
        "block w-full rounded-md border border-line bg-canvas px-3 text-[13px] text-fg transition-colors duration-200 ease-smooth",
        "h-9 placeholder:text-fg-subtle",
        "focus:border-accent focus:bg-surface focus:ring-1 focus:ring-accent focus:outline-none",
        "disabled:cursor-not-allowed disabled:text-fg-subtle",
        className,
      )}
    />
  );
}

/**
 * The native select, styled to sit beside `Input`.
 *
 * Native rather than a listbox built out of divs, and this is a deliberate
 * refusal rather than a shortcut: a custom select has to reimplement typeahead,
 * the escape key, the scroll-into-view of the active option, and the entire
 * mobile picker — and the one on a phone is a full-height wheel that no
 * div-based menu matches. Every filter on these screens is a short list of
 * fixed values, which is exactly what this element is for.
 *
 * `appearance-none` and a drawn chevron, because the platform arrow differs per
 * OS and lands in the padding. `pr-8` reserves the room it draws into.
 */
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        {...rest}
        className={cx(
          "block w-full appearance-none rounded-md border border-line bg-canvas py-0 pr-8 pl-3 text-[13px] text-fg transition-colors duration-200 ease-smooth",
          "h-9",
          "focus:border-accent focus:bg-surface focus:ring-1 focus:ring-accent focus:outline-none",
          "disabled:cursor-not-allowed disabled:text-fg-subtle",
          className,
        )}
      >
        {children}
      </select>
      <svg
        className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-fg-subtle"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}

/**
 * A checkbox and its label, as one target.
 *
 * The whole row is the label, so the text is clickable — a 14px box is under
 * the 24px minimum on its own, and the settings form has three of them.
 */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cx(
        "flex cursor-pointer items-start gap-2.5 rounded-md py-1",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded-sm border-line text-accent accent-accent focus:ring-1 focus:ring-accent focus:outline-none"
      />
      <span className="min-w-0 space-y-0.5">
        <span className="block text-[13px] font-medium text-fg">{label}</span>
        {hint && <span className="block text-[12px] text-fg-subtle">{hint}</span>}
      </span>
    </label>
  );
}

// ------------------------------------------------------------------ states

type AlertTone = "error" | "warn" | "info";

const ALERT_TONES: Record<AlertTone, string> = {
  /* The fills stay light — a /10 tint of these over white is a wash rather than
     a block — while the borders carry the weight, for the reason the button
     variants above record. */
  error: "border-danger/40 bg-danger/8 text-danger",
  warn: "border-warn/40 bg-warn/8 text-warn",
  info: "border-line bg-raised text-fg-muted",
};

export function Alert({ tone = "error", children }: { tone?: AlertTone; children: ReactNode }) {
  return (
    <div className={cx("rounded-md border px-3 py-2 text-[13px]", ALERT_TONES[tone])} role="alert">
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <p className="text-sm text-fg">{title}</p>
      {description && <p className="max-w-md text-[13px] text-fg-muted">{description}</p>}
      {action}
    </div>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation("common");
  return (
    <div className="flex items-center justify-center gap-2 px-6 py-14 text-[13px] text-fg-subtle">
      <Spinner className="size-4" />
      {label ?? t("loading")}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("animate-pulse rounded-md bg-raised", className)} />;
}
